import {
  getFontCss,
  styleConstants,
  themeConstants,
} from '@paywithextend/components'
import React, { useRef } from 'react'
import { useSelector } from 'react-redux'
import ErrorList from 'src/components/ErrorList/ErrorList'
import FieldLabel from 'src/components/FieldLabel/FieldLabel'
import { dcmlFormat } from 'src/localization/Dcml'
import { selectNullSafeLocaleFromState } from 'src/redux/selectors/auth.selectors'
import { css } from 'styled-components'

interface NumberInputFieldProps {
  errors?: Array<React.ReactElement | string>
  fieldLabel?: React.ReactElement | string
  maxValue?: number
  value?: number
  onChange: (value: number) => void
  type?: string
  disabled?: boolean
  className?: string
  autoComplete?: string
  name?: string
  testId?: string
  tooltip?: React.ReactElement
}

const NumberInputField: React.FC<NumberInputFieldProps> = (
  props,
): React.ReactElement => {
  const locale = useSelector(selectNullSafeLocaleFromState)
  const ref = useRef<HTMLInputElement | null>(null)

  const onChange = (
    e: React.ChangeEvent<HTMLInputElement | HTMLTextAreaElement>,
  ): void => {
    const replacedString = e.target.value.replace(/\D/g, '')
    const parsedString = parseInt(replacedString)
    props.onChange(
      props.maxValue !== undefined
        ? Math.min(parsedString, props.maxValue) || 0
        : parsedString || 0,
    )
  }

  const formattedInput = (val: number | undefined): string | undefined => {
    return val !== undefined ? dcmlFormat(locale, val) : undefined
  }

  const onKeyPress = (e: React.KeyboardEvent<HTMLInputElement>): void => {
    if (e.key === 'Enter') {
      e.preventDefault()
      if (document.activeElement === ref.current) {
        ref.current?.blur()
      }
    }
  }

  return (
    <div
      className={props.className}
      css={css`
        display: flex;
        flex-direction: column;
      `}
    >
      {props.fieldLabel !== undefined && (
        <FieldLabel
          css={css`
            z-index: 1;
            display: flex;
            align-items: center;
          `}
          dontHideOverflow
        >
          {props.fieldLabel}
          {props.tooltip !== undefined && props.tooltip}
        </FieldLabel>
      )}
      <input
        value={formattedInput(props.value)}
        type={'text'}
        onChange={onChange}
        disabled={props.disabled}
        autoComplete={props.autoComplete || 'off'}
        name={props.name}
        inputMode={'decimal'}
        ref={ref}
        onKeyPress={onKeyPress}
        data-testid={props.testId}
        css={css`
          width: 100%;
          border-radius: 6px;
          height: 50px;
          border: 1px solid ${styleConstants.colors.gray_medium};
          padding: 20px;
          caret-color: ${themeConstants.colors.cta};
          ${getFontCss(
            14,
            themeConstants.colors.font_primary,
            styleConstants.fontFamilies.regular,
          )};

          background-color: ${styleConstants.colors.gray_lightest};
          &::placeholder {
            opacity: 0.5;
            ${getFontCss(
              14,
              styleConstants.colors.gray_darkest,
              styleConstants.fontFamilies.regular,
            )};
          }

          &:focus,
          &:hover {
            background-color: ${styleConstants.colors.white};
            border: 1px solid ${themeConstants.colors.cta};
          }

          ${props.errors !== undefined &&
          css`
            border: 1px solid ${styleConstants.colors.red};
            ${getFontCss(
              14,
              styleConstants.colors.red,
              styleConstants.fontFamilies.regular,
            )};
          `}
        `}
      />
      {props.errors !== undefined && <ErrorList errors={props.errors} />}
    </div>
  )
}

export default React.memo(NumberInputField)
